export interface WatermarkOptions {
  imageSource: HTMLImageElement | HTMLVideoElement | string;
  accountId: string;
  agentId: string;
  agentName: string;
  date: string;
  time: string;
  /**
   * The real reading, or null/undefined when there is no fix.
   *
   * These used to be required numbers, and anything that was not a number was
   * silently replaced with 19.8762, 75.3433 -- a fixed point in Chhatrapati
   * Sambhajinagar -- and an accuracy of 4.5 m. So a photo taken indoors, or
   * with location permission refused, carried coordinates that LOOKED like a
   * precise reading and were invented. On a field-visit document used in
   * recovery proceedings, that is worse than having no coordinates at all.
   */
  latitude?: number | null;
  longitude?: number | null;
  accuracy?: number | null;
  remark: string;
  visitId?: string;
  customerName?: string;
}

export interface WatermarkResult {
  dataUrl: string;
  watermarkText: string;
  driveFileId: string;
  driveFolder: string;
  date: string;
  time: string;
  /** Null when no fix was obtained. Never a stand-in value. */
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  /** True when the photo was stamped GPS UNAVAILABLE. */
  gpsUnavailable: boolean;
}

/**
 * Generates an official SRMS Geo-tagged Watermarked Photo using HTML5 Canvas.
 */
export async function createSRMSWatermarkedPhoto(
  options: WatermarkOptions
): Promise<WatermarkResult> {
  const {
    imageSource,
    accountId,
    agentId,
    agentName,
    date,
    time,
    latitude,
    longitude,
    accuracy = null,
    remark,
    visitId,
    customerName,
  } = options;

  // A reading or nothing. Never a stand-in.
  const hasFix =
    typeof latitude === 'number' &&
    !isNaN(latitude) &&
    typeof longitude === 'number' &&
    !isNaN(longitude) &&
    // 0,0 is in the Atlantic. It is what an uninitialised value looks like,
    // never a branch in Maharashtra.
    !(latitude === 0 && longitude === 0);
  const safeLat: number | null = hasFix ? (latitude as number) : null;
  const safeLng: number | null = hasFix ? (longitude as number) : null;
  const safeAcc: number | null =
    typeof accuracy === 'number' && !isNaN(accuracy) && accuracy > 0 ? accuracy : null;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return reject(new Error('Canvas 2D context not available'));
    }

    const processCanvas = (img: HTMLImageElement | HTMLVideoElement) => {
      // ---- 1. Size: max 1280px on the long side, aspect ratio preserved ----
      // The old code forced every photo to 1200x900, which stretched portrait
      // shots. Resizing here is also what takes a 3-5 MB camera photo down to
      // roughly 200 KB, which matters on a weak field connection.
      const srcW =
        (img as HTMLVideoElement).videoWidth || (img as HTMLImageElement).naturalWidth || 1280;
      const srcH =
        (img as HTMLVideoElement).videoHeight || (img as HTMLImageElement).naturalHeight || 960;

      const MAX_EDGE = 1280;
      const scale = Math.min(1, MAX_EDGE / Math.max(srcW, srcH));
      const width = Math.max(320, Math.round(srcW * scale));
      const height = Math.max(240, Math.round(srcH * scale));

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      // No crosshair or target marker is drawn. It obscured the subject and
      // carried no information -- the coordinates are in the label below.

      // ---- 2. Overlay: fields plus the agent's note, lower-left ------------
      // Customer name, account number, date & time, latitude/longitude, and
      // the note the agent typed, so the visit outcome is readable from the
      // picture itself without opening the record.
      // The agent's name is deliberately NOT burned into the image; it is
      // stored on the record and shown under the photo instead.
      const pad = Math.round(width * 0.018);
      const boxWidth = Math.min(Math.round(width * 0.58), width - pad * 2);
      // Keyed to the SHORT edge so a portrait photo does not stretch the rows
      // down the label the way keying it to the height did.
      const lineGap = Math.round(Math.min(width, height) * 0.038);
      const boxX = pad;

      const fs = Math.max(11, Math.round(width * 0.0115)); // field font size
      const ls = Math.max(10, Math.round(width * 0.0098)); // label font size

      // Wrap the note before sizing the box, so the box grows to fit it.
      // Capped at three lines; a long note is stored in full on the record.
      const noteText = (remark || '').replace(/\s+/g, ' ').trim();
      const noteGap = Math.round(fs * 1.35);
      ctx.font = `bold ${fs}px system-ui, -apple-system, sans-serif`;
      const noteWidth = boxWidth - pad * 2;
      const clampLine = (text: string): string => {
        let t = text;
        while (t.length > 2 && ctx.measureText(t).width > noteWidth) {
          t = t.slice(0, -2);
        }
        return t.length < text.length ? t + '…' : t;
      };

      const allNoteLines = noteText ? wrapText(ctx, noteText, noteWidth) : [];
      const noteLines = allNoteLines.slice(0, 3).map(clampLine);
      if (allNoteLines.length > 3 && noteLines.length === 3 && !noteLines[2].endsWith('…')) {
        noteLines[2] = clampLine(noteLines[2] + ' …');
      }

      const noteBlock = noteLines.length
        ? Math.round(ls * 1.5 + noteLines.length * noteGap)
        : 0;
      const boxHeight = Math.round(lineGap * 4 + pad * 2.2 + noteBlock);
      const boxY = height - boxHeight - pad;

      ctx.save();

      ctx.fillStyle = 'rgba(15, 23, 42, 0.52)';
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 10);
      ctx.fill();
      ctx.stroke();

      const labelX = boxX + pad;
      const valueX = boxX + pad + Math.round(boxWidth * 0.26);
      let curY = boxY + pad + fs;

      const maxValueWidth = boxWidth - (valueX - boxX) - pad;

      const fit = (text: string): string => {
        let t = text;
        while (t.length > 4 && ctx.measureText(t).width > maxValueWidth) {
          t = t.slice(0, -2);
        }
        return t.length < text.length ? t + '…' : t;
      };

      const row = (label: string, value: string, valueColor: string) => {
        ctx.font = `bold ${ls}px system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = '#94a3b8';
        ctx.fillText(label, labelX, curY);

        ctx.font = `bold ${fs}px system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = valueColor;
        ctx.fillText(fit(value), valueX, curY);

        curY += lineGap;
      };

      row('Customer', customerName || 'Verified Borrower', '#ffffff');
      row('Account', accountId, '#f8fafc');
      row('Date/Time', `${date}  ${time}`, '#f1f5f9');

      ctx.font = `bold ${ls}px system-ui, -apple-system, sans-serif`;
      ctx.fillStyle = '#94a3b8';
      ctx.fillText('GPS', labelX, curY);
      ctx.font = `bold ${fs}px monospace`;
      if (safeLat === null || safeLng === null) {
        // Amber, and it says so. Better an honest gap than a precise lie.
        ctx.fillStyle = '#fbbf24';
        ctx.fillText(fit('GPS UNAVAILABLE'), valueX, curY);
      } else {
        // Accuracy is printed beside the reading, and turns amber when the fix
        // is too coarse to place someone at a door. A reading with no accuracy
        // shown was how a 2 km cell-tower fix passed for a survey point.
        const coarse = safeAcc !== null && safeAcc > 100;
        ctx.fillStyle = coarse ? '#fbbf24' : '#34d399';
        const accText =
          safeAcc === null ? '' : `  ±${safeAcc < 10 ? safeAcc.toFixed(1) : Math.round(safeAcc)}m`;
        ctx.fillText(
          fit(`${safeLat.toFixed(6)}, ${safeLng.toFixed(6)}${accText}`),
          valueX,
          curY
        );
      }

      // ---- Note ------------------------------------------------------------
      // Full width of the box, under a hairline, so a long remark has room to
      // breathe instead of being squeezed into the narrow value column.
      if (noteLines.length) {
        curY += lineGap;

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(labelX, curY - Math.round(fs * 1.2));
        ctx.lineTo(boxX + boxWidth - pad, curY - Math.round(fs * 1.2));
        ctx.stroke();

        ctx.font = `bold ${ls}px system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = '#94a3b8';
        ctx.fillText('NOTE', labelX, curY);

        curY += Math.round(ls * 1.2);

        ctx.font = `bold ${fs}px system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = '#fde68a';
        noteLines.forEach((line) => {
          ctx.fillText(line, labelX, curY);
          curY += noteGap;
        });
      }

      ctx.restore();

      // ---- 3. Export at a field-friendly weight ---------------------------
      // 0.72 keeps the label crisp while bringing the file to roughly 200 KB.
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);

      const driveId = `DRV_IMG_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;

      const watermarkText = `ScaleSupport GPS AUDIT\n\nCustomer: ${customerName || 'Verified Borrower'}\nAccount ID: ${accountId}\nAgent: ${agentId} (${agentName})\n\nDate: ${date}\nTime: ${time}\n\n${
        safeLat === null || safeLng === null
          ? 'Location: GPS UNAVAILABLE (no fix obtained; no coordinates recorded)'
          : `Latitude: ${safeLat.toFixed(6)}\nLongitude: ${safeLng.toFixed(
              6
            )}\nAccuracy: ${safeAcc === null ? 'unknown' : `±${safeAcc.toFixed(1)}m`}`
      }\n\nRemark:\n${remark}`;

      resolve({
        dataUrl,
        watermarkText,
        driveFileId: driveId,
        driveFolder: `ScaleSupport/Accounts/${accountId}/Photos`,
        date,
        time,
        latitude: safeLat,
        longitude: safeLng,
        accuracy: safeAcc,
        gpsUnavailable: safeLat === null || safeLng === null,
      });
    };

    if (typeof imageSource === 'string') {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => processCanvas(img);
      img.onerror = () => reject(new Error('Failed to load image source'));
      img.src = imageSource;
    } else {
      processCanvas(imageSource);
    }
  });
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
}

export function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function calculateCommission(recoveryAmount: number, rate: number = 10): number {
  return Math.round((recoveryAmount * rate) / 100);
}
